/**
 * **Grapple — the bind's rules core.** Pure
 * rules (no I/O, no dice): the partner-lookup helper both the break and release/force-release
 * resolvers share, the legality gates the gameplay-tier intents run before resolving, and the
 * grounded step builder that clears the mutual `Grappled` marker. The room
 * (`backend/.../encounterRoom.ts`) calls these; `resolveContest` (`contest.ts`) supplies the
 * break's opposed-check math.
 *
 * **Attacker identity, for free.** `Grappled.partner` deliberately carries only the
 * relationship pointer, not who initiated — but `StatusInstance.source` is already
 * auto-stamped by `add_status` as the *acting* combatant, and the `GRAPPLE` on-hit effect
 * resolves with `self = attacker` for BOTH sides' `add_status` calls (`resolve/attackTriggerFires.ts`'s on-hit
 * trigger context), so **both markers' `source` is the grappler** — a side effect that gives
 * {@link checkGrappleReleaseLegality} "was `actor` the attacker?" for free, with no extra state.
 */

import {
  type CombatantId,
  type EncounterState,
  type ResolvedStep,
  type StatusInstance,
  IMMOBILIZED_STATUS,
  canUseAction,
  statusFacetsFromInstance,
  turnBudgetVirgin,
  GRAPPLE_DOMINANCE_STATUS,
  GRAPPLE_SIZE_EDGE_STATUS,
  GRAPPLE_SIZE_PARITY_STATUS,
  grappleDominantOf,
  grapplePartner,
  grappleSizeOrder,
  grappledStatus,
} from "@lancer-vtt/domain";
import { type Result, ok, err } from "@lancer-vtt/shared";
import { affordable } from "./events/gameEvents";

// Dominance lives in the domain, whose movement context reads it (`grappleDominance.ts`).
export { GRAPPLE_DOMINANCE_STATUS, grappleDominantOf, grapplePartner };

/** The composite marker `act_grapple`'s on-hit effect binds both parties with. */
export const GRAPPLED_STATUS = "Grappled";

/**
 * May `actor` attempt `request_grapple_break`? Legal IFF, in order:
 *
 *   1. **`actor` is acting** — its own turn (`activation.turn.phase === "acting"` and it is the
 *      acting combatant). Break Grapple is a **Quick action** (Core Book p.72), so it costs the
 *      defender a quick action point on ITS OWN turn — it can't be taken off-turn. This gate is
 *      explicit here (mirroring {@link checkGrappleContestLegality}) rather than left to
 *      {@link checkBudget}, because `checkBudget` is deliberately **inert while `phase !== "acting"`**
 *      (the pre-fight/between-activation setup window) — so without this, a Break Grapple clicked
 *      while no unit is activated slips through both gates and resolves off-turn.
 *   2. **`actor` carries a `Grappled` marker with a resolvable partner** — modeled symmetrically:
 *      whichever party calls this is the contest's challenger, the other its defender, matching
 *      `resolveContest`'s consumer-resolved shape.
 */
export function checkGrappleBreakLegality(state: EncounterState, actor: string): Result<void> {
  const turn = state.activation.turn;
  if (turn.phase !== "acting" || turn.actor !== actor) {
    return err(`${actor} is not acting — Break Grapple is a Quick action, only legal on your own turn`);
  }
  const g = grappledStatus(state, actor);
  if (!g) return err(`${actor} is not Grappled`);
  const partner = g.partner;
  if (partner === undefined || state.combatants[partner] === undefined) {
    return err(`${actor}'s Grappled marker has no resolvable partner`);
  }
  return ok(undefined);
}

/**
 * May `actor` `request_grapple_release`? RAW (Core p.72): only the grapple's ATTACKER may end
 * it unconditionally as a free action — the defender must break free via contested HULL
 * instead ({@link checkGrappleBreakLegality}). `g.source === actor` identifies the attacker
 * (see this module's doc for why `source`, not `partner`, carries that fact).
 */
export function checkGrappleReleaseLegality(state: EncounterState, actor: string): Result<void> {
  const g = grappledStatus(state, actor);
  if (!g) return err(`${actor} is not Grappled`);
  if (g.source !== actor) {
    return err(
      `${actor} did not initiate this grapple — only the attacker may release it as a free action (the defender must break free instead)`,
    );
  }
  return ok(undefined);
}

/**
 * The once-per-turn ledger id the contest resolver folds a zero-point `spend_action` under
 * (`refereeContestAction.ts`), and which {@link checkGrappleContestLegality} reads through
 * {@link canUseAction} to cap the contest at one per turn. **Both sides must name the same
 * string**, so it is a shared const rather than two literals.
 */
export const GRAPPLE_CONTEST_ACTION_ID = "act_grapple_contest";

/**
 * May `actor` dispatch `request_grapple_contest` — the same-Size turn-start contested-HULL
 * tiebreak (Core Book p.69)? Legal IFF, in order:
 *
 *   1. **`actor` is acting** — its own turn, `activation.turn.phase === "acting"`.
 *   2. **`actor` carries a `Grappled` marker with a resolvable partner** (same check as
 *      {@link checkGrappleBreakLegality}).
 *   3. **Raw sizes are EQUAL** (`base.size`) — an unequal pair already has a size-derived
 *      dominant ({@link grappleDominantOf}'s rule 1); nothing to contest.
 *   4. **Nothing spent yet this turn** — RAW's "at the start of their turn", read through
 *      `@lancer-vtt/domain`'s {@link turnBudgetVirgin}, the ONE shared door Ordnance's firing
 *      window and Protocol timing read too. This module used to carry a hand-copied leaf
 *      duplicate of that predicate to dodge an import cycle; the cost-keyed version is pure
 *      over `TurnBudget` and lives in `domain`, so the reason to duplicate is gone and the
 *      copy was deleted.
 *   5. **The contest hasn't already been used this turn** ({@link canUseAction} on
 *      {@link GRAPPLE_CONTEST_ACTION_ID}). **This leg is load-bearing and was NOT here before**:
 *      the contest is RAW-free (0 action points), so under the old `actionUses`-reading window
 *      leg 4 doubled as the once-per-turn cap — the resolver's zero-point
 *      `spend_action{actionId: "act_grapple_contest"}` dirtied `actionUses` and closed the
 *      window. The cost-keyed window no longer reads that ledger, and a free action leaves the
 *      spend counter at 0, so without this explicit read a second contest in the same turn
 *      would become legal. The ledger stamp the resolver already folds is now READ rather than
 *      merely documenting intent.
 */
export function checkGrappleContestLegality(state: EncounterState, actor: string): Result<void> {
  const turn = state.activation.turn;
  if (turn.phase !== "acting" || turn.actor !== actor) {
    return err(`${actor} is not acting — the same-Size contest is only legal on your own turn`);
  }
  const g = grappledStatus(state, actor);
  if (!g) return err(`${actor} is not Grappled`);
  const partner = g.partner;
  if (partner === undefined || state.combatants[partner] === undefined) {
    return err(`${actor}'s Grappled marker has no resolvable partner`);
  }
  const ca = state.combatants[actor];
  const cb = state.combatants[partner];
  if (ca === undefined || cb === undefined) {
    return err(`${actor} or ${partner} is not on the map`);
  }
  if (grappleSizeOrder(state, actor, partner) !== 0) {
    return err(`${actor} and ${partner} are not the same Size — dominance is already Size-derived, nothing to contest`);
  }
  if (!turnBudgetVirgin(turn.budget)) {
    return err(`${actor} has already acted this turn — the same-Size contest is only legal before spending anything`);
  }
  if (!affordable({ kind: "action_points", carrier: actor, points: 0, actionId: GRAPPLE_CONTEST_ACTION_ID }, state, undefined)) {
    return err(`${actor} has already contested this grapple this turn (once per turn)`);
  }
  return ok(undefined);
}

/**
 * The `status_remove` for a live grapple-side instance, carrying its facets
 * ({@link statusFacetsFromInstance}). Without them the undo re-adds `Grappled` with no `partner` or
 * `source`: a grapple nothing can contest, break, release or end by adjacency
 * (`projects/grapple-move-feel.md` Defect 1).
 */
export function statusRemoveStep(target: CombatantId, inst: StatusInstance): ResolvedStep {
  return { op: "status_remove", target, status: inst.name, ...statusFacetsFromInstance(inst) };
}

/**
 * The grounded steps that clear the mutual `Grappled` marker off both `a` and `b` — the shared
 * consequence a breaker's win-or-tie, an unconditional release, and the broken-adjacency
 * recomputer all apply. Idempotent per side (a party not currently carrying `Grappled` yields
 * no step for it) and **sorted by id** for deterministic, replay-stable step order (mirrors
 * `engagementRecomputer`'s discipline), not call-argument order.
 *
 * Grapple end also
 * clears each side's {@link GRAPPLE_DOMINANCE_STATUS} marker (if any — a same-Size contest
 * winner) and its **partner-matched** grapple-caused `Immobilized` (`immobilized.partner ===
 * other`, the provenance mark {@link grappleImmobilizedSyncSteps} stamps it with). The
 * partner-match, not a bare name match, is the non-clobber invariant: a GM-applied Immobilized
 * (no `partner`) never satisfies it, so it survives grapple end untouched.
 */
/** The partner-stamped grapple markers grapple end clears: the contest winner's, and the Size claims. */
const GRAPPLE_MARKERS = new Set([GRAPPLE_DOMINANCE_STATUS, GRAPPLE_SIZE_EDGE_STATUS, GRAPPLE_SIZE_PARITY_STATUS]);

export function grappleClearSteps(state: EncounterState, a: CombatantId, b: CombatantId): ResolvedStep[] {
  const steps: ResolvedStep[] = [];
  for (const id of [a, b].sort()) {
    const other = id === a ? b : a;
    const c = state.combatants[id];
    if (c === undefined) continue;
    const grappled = c.statuses.find((s) => s.name === GRAPPLED_STATUS);
    if (grappled !== undefined) steps.push(statusRemoveStep(id, grappled));
    for (const marker of c.statuses.filter((s) => GRAPPLE_MARKERS.has(s.name))) steps.push(statusRemoveStep(id, marker));
    const immobilized = c.statuses.find((s) => s.name === IMMOBILIZED_STATUS);
    if (immobilized !== undefined && immobilized.partner === other) steps.push(statusRemoveStep(id, immobilized));
  }
  return steps;
}

/**
 * The grounded steps a Grapple release/force-release applies: clear `Grappled` on `actor` and
 * its partner (read off `actor`'s own marker). Idempotent — a non-Grappled `actor` yields no
 * steps (so the room broadcasts nothing), same posture as `hideSteps`/`clearHiddenSteps`. Both
 * the gameplay `request_grapple_release` and a GM-forced release (`gm_force`) apply these same
 * steps; only the gameplay tier runs {@link checkGrappleReleaseLegality} first.
 */
export function grappleReleaseSteps(state: EncounterState, actor: string): ResolvedStep[] {
  const partner = grapplePartner(state, actor);
  if (partner === undefined) return [];
  return grappleClearSteps(state, actor, partner);
}

/**
 * **The grapple-caused Immobilized sync** — a pure full-sweep diff, mirroring
 * {@link "./spatialRecompute".engagementRecomputer}'s shape (sorted ids, idempotent): for every
 * combatant, stamp a partner-provenance `Immobilized` on the non-dominant party of a grapple and
 * clear orphaned grapple-caused `Immobilized` / `Grapple Dominance` markers once the pair or the
 * dominance no longer justifies them. Dominance is read **only** through {@link grappleDominantOf}
 * — never a raw `base.size` compare or raw status lookup.
 *
 * Fired at every dominance-changing seam: post-attack (the `act_grapple` on-hit bind), the
 * same-Size contest resolution, and post-move after the adjacency-end recomputer.
 *
 * **Non-clobber invariant (test-pinned):** the smaller/loser party's `Immobilized` is stamped
 * with `partner` = the *dominant's* id — the provenance mark. Clearing an `Immobilized` requires
 * `immobilized.partner !== undefined` (grapple-caused); a GM-applied `Immobilized` (no `partner`)
 * is never touched. Stamping is also non-clobbering the other direction: `status_add` dedups by
 * NAME (a known limitation of the by-name status model), so a pre-existing `Immobilized` of
 * ANY provenance (GM's included) silently absorbs the grapple's own stamp rather than being
 * overwritten — correct, since the GM's persists through grapple end.
 */
export function grappleImmobilizedSyncSteps(state: EncounterState): ResolvedStep[] {
  const steps: ResolvedStep[] = [];
  for (const id of Object.keys(state.combatants).sort()) {
    const c = state.combatants[id];
    if (c === undefined) continue;
    const g = c.statuses.find((s) => s.name === GRAPPLED_STATUS);
    const partner = g?.partner;
    const grappled = partner !== undefined && state.combatants[partner] !== undefined;
    const dominant = grappled ? grappleDominantOf(state, id, partner) : undefined;
    const shouldBeImmobilized = dominant !== undefined && dominant !== id;

    const immobilized = c.statuses.find((s) => s.name === IMMOBILIZED_STATUS);
    if (shouldBeImmobilized) {
      if (immobilized === undefined) {
        steps.push({
          op: "status_add",
          target: id,
          status: IMMOBILIZED_STATUS,
          expiry: { kind: "until_removed" },
          partner: dominant,
        });
      } else if (immobilized.partner !== undefined && immobilized.partner !== dominant) {
        // A stale grapple-caused stamp pointing at a former dominant (e.g. after a same-Size
        // dominance flip) — re-stamp with the current dominant's id. A GM-applied
        // Immobilized (no `partner`) never reaches this branch, so it's never touched.
        steps.push(statusRemoveStep(id, immobilized));
        steps.push({
          op: "status_add",
          target: id,
          status: IMMOBILIZED_STATUS,
          expiry: { kind: "until_removed" },
          partner: dominant,
        });
      }
      // Otherwise already correctly stamped, or GM-applied (leave alone either way).
    } else if (immobilized !== undefined && immobilized.partner !== undefined) {
      // No longer justified (not grappled, dominant itself, or an unresolved same-Size pair) —
      // clear only the grapple-caused stamp (provenance-gated on `partner`, never by name alone).
      steps.push(statusRemoveStep(id, immobilized));
    }

    const dominanceMarker = c.statuses.find((s) => s.name === GRAPPLE_DOMINANCE_STATUS);
    if (dominanceMarker !== undefined) {
      const justified = grappled && dominanceMarker.partner === partner && grappleSizeOrder(state, id, partner) === 0;
      if (!justified) steps.push(statusRemoveStep(id, dominanceMarker));
    }
    // A Size claim (Synthetic Muscle Netting) lasts only as long as the grapple with its partner.
    for (const claim of c.statuses.filter((s) => s.name === GRAPPLE_SIZE_EDGE_STATUS || s.name === GRAPPLE_SIZE_PARITY_STATUS)) {
      if (!grappled || claim.partner !== partner) steps.push(statusRemoveStep(id, claim));
    }
  }
  return steps;
}
